import {
  aggregateWeekly,
  BODY_MEASUREMENT_CATALOG,
  type BodyMeasurementEntryData,
  type BodyMeasurementTypeKey,
  type TrendWeek,
} from "@/lib/body-measurements";
import { addDays, isoWeekMonday, isoWeekOf } from "@/lib/days";
import { scoreRefs } from "@/lib/ref-scoring";
import type {
  WeekData,
  WeeklyPointSummaryConfig,
  WidgetConfig,
} from "@/lib/schema";
import { isWeekFrozen } from "@/lib/week-updates";

/*
 * Sykluser (docs/cycle-design.md, CONTEXT.md «Cycles»). A cycle row stores
 * only its definition; every figure here is derived on read from the weeks'
 * snapshots and the global body measurement entries (docs/adr/0027).
 */

export const CYCLE_TYPE = "cycle";
export const DEFAULT_CYCLE_LENGTH = 8;

export function cycleId(uuid: string): string {
  return `cycle:${uuid}`;
}

export type JudgingRule = "samlet" | "uke-for-uke";

/**
 * Points at exactly one of: a widget instance or part (`ref`), or a whole
 * Summary row (`summaryRowId`), followed by id through each week's snapshot.
 */
export interface PointsTarget {
  id: string;
  kind: "points";
  /** formatWidgetRef(widgetId, partKey?) — the Summary row format. */
  ref?: string;
  summaryRowId?: string;
  requiredPercent: number;
  rule: JudgingRule;
  /** Uke for uke only; absent means every week of the cycle. */
  requiredWeeks?: number;
}

export interface MeasurementTarget {
  id: string;
  kind: "measurement";
  typeKey: BodyMeasurementTypeKey;
  /** Signed, in the type's unit; never 0. Negative means a decrease. */
  wantedChange: number;
}

export type CycleTarget = PointsTarget | MeasurementTarget;

export interface CycleData {
  startYear: number;
  startWeekNumber: number;
  length: number;
  name: string;
  note: string;
  archived: boolean;
  targets: CycleTarget[];
}

export interface CycleRecord {
  id: string;
  data: CycleData;
}

// ---------- ISO week span ----------

/** Local midnight for a YYYY-MM-DD, so `isoWeekOf` reads the intended day. */
function dateOf(isoDate: string): Date {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** YYYY-MM-DD for a Date, read in local time. */
function localIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function isoWeekAtMonday(monday: string): TrendWeek {
  const { year, weekNumber } = isoWeekOf(dateOf(monday));
  return { year, weekNumber, monday };
}

/** Monday of the week the cycle starts in. */
export function cycleStartMonday(cycle: CycleData): string {
  return isoWeekMonday(cycle.startYear, cycle.startWeekNumber);
}

/** Monday of the cycle's last week. */
export function cycleLastMonday(cycle: CycleData): string {
  return addDays(cycleStartMonday(cycle), (cycle.length - 1) * 7);
}

/** The cycle's ISO weeks, oldest first. */
export function cycleWeeks(cycle: CycleData): TrendWeek[] {
  const start = cycleStartMonday(cycle);
  return Array.from({ length: cycle.length }, (_, i) =>
    isoWeekAtMonday(addDays(start, i * 7)),
  );
}

/** The Utgangspunkt week: the ISO week immediately before the cycle. */
export function baselineWeek(cycle: CycleData): TrendWeek {
  return isoWeekAtMonday(addDays(cycleStartMonday(cycle), -7));
}

function cyclesOverlap(a: CycleData, b: CycleData): boolean {
  return (
    cycleStartMonday(a) <= cycleLastMonday(b) &&
    cycleStartMonday(b) <= cycleLastMonday(a)
  );
}

/** Avsluttet: the cycle's last week has ended. Derived, never stored. */
export function isCycleEnded(cycle: CycleData, today: Date): boolean {
  return addDays(cycleLastMonday(cycle), 6) < localIsoDate(today);
}

function weekEnded(monday: string, today: Date): boolean {
  return addDays(monday, 6) < localIsoDate(today);
}

/** "Uke 44–51 · 2026", or across a year boundary "Uke 50 · 2026 – uke 5 · 2027". */
export function cycleWeeksLabel(cycle: CycleData): string {
  const weeks = cycleWeeks(cycle);
  const first = weeks[0];
  const last = weeks[weeks.length - 1];
  if (weeks.length === 1) return `Uke ${first.weekNumber} · ${first.year}`;
  if (first.year === last.year) {
    return `Uke ${first.weekNumber}–${last.weekNumber} · ${first.year}`;
  }
  return `Uke ${first.weekNumber} · ${first.year} – uke ${last.weekNumber} · ${last.year}`;
}

/** "28. sep. 2026 – 25. okt. 2026": the cycle's first Monday to last Sunday. */
export function cycleDateRangeLabel(cycle: CycleData): string {
  const format = new Intl.DateTimeFormat("nb-NO", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const first = dateOf(cycleStartMonday(cycle));
  const last = dateOf(addDays(cycleLastMonday(cycle), 6));
  return `${format.format(first)} – ${format.format(last)}`;
}

export function cycleLabel(cycle: CycleData): string {
  return cycle.name.trim() || cycleWeeksLabel(cycle);
}

/**
 * Where «Ny syklus» starts by default: the first week after the latest
 * cycle, but never earlier than next week.
 */
export function defaultCycleStart(cycles: CycleData[], today: Date): TrendWeek {
  const thisWeek = isoWeekOf(today);
  let monday = addDays(isoWeekMonday(thisWeek.year, thisWeek.weekNumber), 7);
  for (const cycle of cycles) {
    const after = addDays(cycleLastMonday(cycle), 7);
    if (after > monday) monday = after;
  }
  return isoWeekAtMonday(monday);
}

export function sortCyclesByStart(cycles: CycleRecord[]): CycleRecord[] {
  return [...cycles].sort((a, b) =>
    cycleStartMonday(a.data) < cycleStartMonday(b.data) ? -1 : 1,
  );
}

// ---------- Week cells ----------

interface WeekRecordLike {
  id: string;
  data: WeekData;
}

/** A cycle week with the Week row that holds it, if one exists. */
export interface CycleWeek extends TrendWeek {
  week: WeekRecordLike | null;
}

export function attachWeeks(
  weeks: TrendWeek[],
  records: WeekRecordLike[],
): CycleWeek[] {
  return weeks.map((w) => ({
    ...w,
    week:
      records.find(
        (r) => r.data.year === w.year && r.data.weekNumber === w.weekNumber,
      ) ?? null,
  }));
}

/**
 * - `future`: no row yet and the week has not passed — it may still happen.
 * - `ikke-med`: nothing to measure — no row for a past week, or the
 *   references resolve to no Week goal in the snapshot.
 * - `hittil`: the week exists but is not Frozen; shown, counts for nothing.
 * - `avgjort`: Frozen; counts toward the target.
 */
interface MeasuredCell {
  kind: "hittil" | "avgjort";
  points: number;
  goal: number;
  percent: number;
}

export type WeekCell = { kind: "future" } | { kind: "ikke-med" } | MeasuredCell;

function weekCell(refs: string[], cycleWeek: CycleWeek, today: Date): WeekCell {
  const { week } = cycleWeek;
  if (!week) {
    const todayWeek = isoWeekOf(today);
    const thisMonday = isoWeekMonday(todayWeek.year, todayWeek.weekNumber);
    return cycleWeek.monday >= thisMonday
      ? { kind: "future" }
      : { kind: "ikke-med" };
  }
  const { score, expected } = scoreRefs(refs, week.data.widgets);
  if (expected <= 0) return { kind: "ikke-med" };
  return {
    kind: isWeekFrozen(week.data, today) ? "avgjort" : "hittil",
    points: score,
    goal: expected,
    percent: (score / expected) * 100,
  };
}

// ---------- Judging ----------

/**
 * - `met` / `not-met`: settled for good.
 * - `on-track` / `behind`: Samlet while weeks remain — the figure so far
 *   against the bar.
 * - `open`: nothing to say yet.
 * - `missing`: a Measurement target with fewer than two weeks of entries.
 */
export type TargetStatus =
  | "met"
  | "not-met"
  | "on-track"
  | "behind"
  | "open"
  | "missing";

interface PointsJudgement {
  status: TargetStatus;
  /** Samlet: the cycle percentage over Avgjort weeks, null before any. */
  percent: number | null;
  /** Uke for uke only. */
  passed: number;
  required: number;
  /** Weeks not yet Avgjort: the current one and those ahead. */
  remaining: number;
}

export function requiredWeeksOf(
  target: PointsTarget,
  cycle: CycleData,
): number {
  return Math.min(target.requiredWeeks ?? cycle.length, cycle.length);
}

/**
 * Points over Week goals across the Avgjort weeks — Samlet's figure, and the
 * result row's for any points column. Null before any week is settled.
 */
export function samletPercent(cells: WeekCell[]): number | null {
  const settled = cells.filter((c): c is MeasuredCell => c.kind === "avgjort");
  const goal = settled.reduce((sum, c) => sum + c.goal, 0);
  const points = settled.reduce((sum, c) => sum + c.points, 0);
  return goal > 0 ? (points / goal) * 100 : null;
}

/** Avgjort weeks so far — what «x av y» counts against in the result row. */
export function settledWeekCount(cells: WeekCell[]): number {
  return cells.filter((c) => c.kind === "avgjort").length;
}

export function judgePointsTarget(
  target: PointsTarget,
  cycle: CycleData,
  cells: WeekCell[],
): PointsJudgement {
  const settled = cells.filter((c): c is MeasuredCell => c.kind === "avgjort");
  const remaining = cells.filter(
    (c) => c.kind === "hittil" || c.kind === "future",
  ).length;
  const required = requiredWeeksOf(target, cycle);
  const passed = settled.filter(
    (c) => c.percent >= target.requiredPercent,
  ).length;

  if (target.rule === "uke-for-uke") {
    let status: TargetStatus = "open";
    if (passed >= required) status = "met";
    else if (passed + remaining < required) status = "not-met";
    return { status, percent: null, passed, required, remaining };
  }

  const percent = samletPercent(cells);
  let status: TargetStatus = "open";
  if (percent !== null) {
    const reached = percent >= target.requiredPercent;
    if (remaining === 0) status = reached ? "met" : "not-met";
    else status = reached ? "on-track" : "behind";
  }
  return { status, percent, passed, required, remaining };
}

interface MeasurementJudgement {
  status: TargetStatus;
  /** Weeks whose averages were compared, or null when missing. */
  from: TrendWeek | null;
  to: TrendWeek | null;
  change: number | null;
  percent: number | null;
}

export interface MeasuredChange {
  /** Weeks whose averages were compared. */
  from: TrendWeek;
  to: TrendWeek;
  change: number;
  /** The change spread over the weeks between `from` and `to`. */
  perWeek: number;
}

/**
 * From the Utgangspunkt week to the cycle's last week, average against
 * average, using only weeks that have ended. A span with no entries at an
 * end shrinks inward to the nearest week that has them; fewer than two
 * such weeks measure nothing.
 */
export function measuredChange(
  typeKey: BodyMeasurementTypeKey,
  cycle: CycleData,
  entries: BodyMeasurementEntryData[],
  today: Date,
): MeasuredChange | null {
  const span = [baselineWeek(cycle), ...cycleWeeks(cycle)].filter((w) =>
    weekEnded(w.monday, today),
  );
  const averages = aggregateWeekly(entries, typeKey, span);
  const measured = span.flatMap((week, i) => {
    const aggregate = averages[i];
    return aggregate ? [{ week, average: aggregate.average }] : [];
  });
  if (measured.length < 2) return null;
  const first = measured[0];
  const last = measured[measured.length - 1];
  const change = last.average - first.average;
  const weeksBetween =
    (Date.parse(`${last.week.monday}T00:00:00Z`) -
      Date.parse(`${first.week.monday}T00:00:00Z`)) /
    (7 * 86400000);
  return {
    from: first.week,
    to: last.week,
    change,
    perWeek: change / Math.round(weeksBetween),
  };
}

export function judgeMeasurementTarget(
  target: MeasurementTarget,
  cycle: CycleData,
  entries: BodyMeasurementEntryData[],
  today: Date,
): MeasurementJudgement {
  const measured = measuredChange(target.typeKey, cycle, entries, today);
  if (!measured) {
    return {
      status: "missing",
      from: null,
      to: null,
      change: null,
      percent: null,
    };
  }
  const { from, to, change } = measured;
  const percent = (change / target.wantedChange) * 100;
  const status: TargetStatus = isCycleEnded(cycle, today)
    ? percent >= 100
      ? "met"
      : "not-met"
    : "open";
  return { status, from, to, change, percent };
}

// ---------- Grid columns ----------

/** Each catalog type's average per week, index-aligned with `weeks`. */
export function measurementAverages(
  entries: BodyMeasurementEntryData[],
  weeks: TrendWeek[],
): Record<BodyMeasurementTypeKey, Array<number | null>> {
  const result = {} as Record<BodyMeasurementTypeKey, Array<number | null>>;
  for (const type of BODY_MEASUREMENT_CATALOG) {
    result[type.key] = aggregateWeekly(entries, type.key, weeks).map(
      (a) => a?.average ?? null,
    );
  }
  return result;
}

export interface SummaryRowColumn {
  rowId: string;
  /** The row's name in the latest week it appears in. */
  name: string;
  instanceId: string;
  instanceLabel: string;
}

function summaryInstances(week: WeekData): WeeklyPointSummaryConfig[] {
  return week.widgets
    .map((e) => e.config)
    .filter(
      (c): c is WeeklyPointSummaryConfig => c.kind === "weekly-point-summary",
    );
}

/**
 * Every Summary row in any of the cycle's weeks, matched by row id across
 * snapshots, ordered by first appearance and grouped by summary instance.
 */
export function summaryRowColumns(weeks: CycleWeek[]): SummaryRowColumn[] {
  const columns = new Map<string, SummaryRowColumn>();
  for (const { week } of weeks) {
    if (!week) continue;
    for (const instance of summaryInstances(week.data)) {
      for (const row of instance.rows) {
        columns.set(row.id, {
          rowId: row.id,
          name: row.name,
          instanceId: columns.get(row.id)?.instanceId ?? instance.id,
          instanceLabel: instance.label,
        });
      }
    }
  }
  const all = [...columns.values()];
  const instanceOrder = [...new Set(all.map((c) => c.instanceId))];
  return instanceOrder.flatMap((id) => all.filter((c) => c.instanceId === id));
}

/** A Summary row's references in one week's snapshot, or null when absent. */
function summaryRowRefs(rowId: string, week: WeekData): string[] | null {
  for (const instance of summaryInstances(week)) {
    const row = instance.rows.find((r) => r.id === rowId);
    if (row) return row.widgetIds;
  }
  return null;
}

export function summaryRowCell(
  rowId: string,
  cycleWeek: CycleWeek,
  today: Date,
): WeekCell {
  if (!cycleWeek.week) return weekCell([], cycleWeek, today);
  const refs = summaryRowRefs(rowId, cycleWeek.week.data);
  if (refs === null) return { kind: "ikke-med" };
  return weekCell(refs, cycleWeek, today);
}

/** One week's cell for a Points target, whatever it points at. */
export function pointsTargetCell(
  target: PointsTarget,
  cycleWeek: CycleWeek,
  today: Date,
): WeekCell {
  if (target.summaryRowId !== undefined) {
    return summaryRowCell(target.summaryRowId, cycleWeek, today);
  }
  return weekCell(
    target.ref === undefined ? [] : [target.ref],
    cycleWeek,
    today,
  );
}

// ---------- Target picker ----------

/**
 * Summary rows a Points target may follow: those in the Ukesmal's enabled
 * summary instances, plus any found only in the cycle's weeks' snapshots.
 */
export function targetCandidateSummaryRows(
  ukesmal: WidgetConfig[],
  weeks: CycleWeek[],
): SummaryRowColumn[] {
  const fromMal = ukesmal
    .filter(
      (c): c is WeeklyPointSummaryConfig =>
        c.kind === "weekly-point-summary" && c.enabled,
    )
    .flatMap((instance) =>
      instance.rows.map((row) => ({
        rowId: row.id,
        name: row.name,
        instanceId: instance.id,
        instanceLabel: instance.label,
      })),
    );
  const offered = new Set(fromMal.map((r) => r.rowId));
  return [
    ...fromMal,
    ...summaryRowColumns(weeks).filter((c) => !offered.has(c.rowId)),
  ];
}

/**
 * Widgets a Points target may reference: the Ukesmal's, plus any instance
 * found only in the cycle's weeks' snapshots (latest snapshot wins).
 */
export function targetCandidateWidgets(
  ukesmal: WidgetConfig[],
  weeks: CycleWeek[],
): WidgetConfig[] {
  const byId = new Map(ukesmal.map((w) => [w.id, w]));
  for (const { week } of [...weeks].reverse()) {
    if (!week) continue;
    for (const entry of week.data.widgets) {
      if (!byId.has(entry.config.id)) byId.set(entry.config.id, entry.config);
    }
  }
  return [...byId.values()];
}

// ---------- Validation ----------

/** Why `candidate` cannot be saved beside `others`, or null. */
export function cycleError(
  candidate: CycleData,
  others: CycleData[],
): string | null {
  if (!Number.isInteger(candidate.length) || candidate.length < 1) {
    return "En syklus må ha minst én uke.";
  }
  const clash = others.find((o) => cyclesOverlap(candidate, o));
  if (clash) {
    return `Overlapper med «${cycleLabel(clash)}».`;
  }
  return null;
}

/** Shortening a cycle may leave a target asking for more weeks than exist. */
export function clampTargets(cycle: CycleData): CycleData {
  return {
    ...cycle,
    targets: cycle.targets.map((t) =>
      t.kind === "points" &&
      t.requiredWeeks !== undefined &&
      t.requiredWeeks > cycle.length
        ? { ...t, requiredWeeks: cycle.length }
        : t,
    ),
  };
}
